import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

/** Id of a conversation in RTC. */
export type RTCConversationId = number
/** Id of a user in RTC. */
export type RTCUserId = number
/** Id of a message in RTC. */
export type RTCMessageId = number
/** Id of a rich content in RTC. */
export type RTCContentId = number

/** * Conversation as defined by RTC. */
export interface RTCConversation {
  /** * Id in RTC database. */
  id: RTCConversationId
  /** Title. */
  title: string
  /** If the conversation is 1-1. */
  isPrivate: boolean
  /** If the conversation is visible. */
  isHidden: boolean
  /** If the conversation is muted */
  isMuted: boolean
  /** Id of the user who created the conversation. */
  creator: RTCUserId
  /** Position in favorite list. */
  favorite: number
  /** last message in conversation. */
  lastMsg: RTCMessage
  /** Id of the last message in conversation. */
  lastMsgId: RTCMessageId
  /** Id of last message read by the current user in the conversation. */
  lastMsgReadId: RTCMessageId
  /** Date the conversation was created on. */
  dateCreation: string
  /** Date the last message was published on. */
  dateLastMsgCreate: string
  /** Date the current user last accessed the conversation on. */
  dateLastAccess: string
  /** Id of last messages read for each member of the conversation. */
  usersLastMsgRead: Record<RTCUserId, RTCMessageId>[]
  /** If automatic removal is activated. */
  automaticRemoval: boolean
  /** List of messages in the conversation. */
  messages: RTCMessage[]
  /** List of tenants of the conversation. Currently only 1. */
  tenants: RTCTenant[]
  /** List of users who are member of the conversation. */
  members: RTCUser[]
  /** List of users who were member of the conversation and are now removed. */
  removedMembers?: RTCUser[]
  /** Subject uri of the conversation, as per defined by 3DSwym. */
  subjectUri: string
  /** If the current user has subscribed to new content published in conversation. */
  isSubscribed: boolean
}

/** Tenant as defined by RTC. */
interface RTCTenant {
  /** Id in RTC database. */
  id: number
  /** Name of the tenant as defined by 3DCompass. */
  name: string
}

/** User as defined by RTC. */
export interface RTCUser {
  /** Id in RTC database. */
  id: RTCUserId
  /** Login. */
  login: string
  /** Uuid as defined by 3DCompass. */
  uuid: string
  /** First name. */
  firstName: string
  /** Last name. */
  lastName: string
  /** Email. */
  email: string
  /** Phone number. */
  phone: string
  /** City name. */
  city: string
  /** Country name. */
  country: string
  convsLastMsgRead: Record<number, number>[]
}

/** Message as defined by RTC. */
export interface RTCMessage {
  /** Id in RTC database. */
  id: RTCMessageId
  /** User who published the message. */
  author: RTCUserId
  /** Id of the conversation the message was published in. */
  conv: RTCConversationId
  /** Date of message publication. */
  creationDate: string
  /** Parent message in case of reply. */
  parentMessage?: RTCMessage
  /** List of users who liked. */
  likes: RTCUser[]
  /** Date of message edition. */
  editionDate?: string
  /** List of contents in message. Currently only 1. */
  contents: RTCContent[]
  /** If forwarded message, the original author of the message. */
  forwardedFrom?: RTCUser
}

/** Rich content as defined by RTC. */
export interface RTCContent {
  /** Id in RTC database. */
  id: RTCContentId
  /** Type of content. */
  type: 'internal' | 'text' | 'textContent' | 'post' | 'media' | 'wedo' | 'ritual' | 'comment' | 'transient'
  /** JWT to fetch the comment. */
  auth?: string
  /** Content data. */
  data: string
  /** Source of the data, e.g. 'swym'. */
  dataSource?: string
  /** Subject uri of the content, as per defined by 3DSwym. */
  subjectUri: string
  /** Content metadata */
  metadata?: {
    /**
     * contentType of the content
     * @note for a meeting topic comment it will be CONFERENCE
     */
    contentType?: 'CONFERENCE' | 'WEDO' | 'POST' | 'MEDIA'
    /**
     *  Subject uri of content
     *  @note for a meeting topic comment it will be the meeting subject_uri and not the topic subject_uri
     */
    subjectUri?: SUBJECT_6W_URI
  }
}

/** Internal event as defined by RTC. */
export interface RTCInternalEvent {
  /** Type of internal event. */
  eventType: RTCInternalEventType
  /** Data for NLS. */
  eventData: any[]
}

/** Internal events sent by RTC. */
export enum RTCInternalEventType {
  /** When: conversation creation. */
  convCreated = 'convCreated',
  /** When: conversation title update. */
  titleUpdated = 'titleUpdated',
  /** When: user added to conversation. */
  usersAdded = 'usersAdded',
  /** When: user removed from conversation. */
  userRemoved = 'userRemoved',
  /** When: activation/desactivation of conversation automatic removal. */
  updateAutoRemoval = 'updateAutoRemoval',
  /** When: comment on rich content. */
  comment = 'comment',
  /** When: message deletion. */
  msgDeleted = 'msgDeleted',
  /** When: wedo maturity change. */
  changeStatus = 'changeStatus',
  /** When: audio/video call started. */
  startedCall = 'startedCall',
  /** When: audio/video call started but no one joined. */
  startedCallEmpty = 'startedCallEmpty',
  /** When: content edition. */
  contentEdited = 'contentEdited',
  /** Triggered from Rich Clients */
  catiaCollab = 'catiaCollab',
}

/** Conversation thread in a section. */
export interface RTCSectionThread {
  /** Conversation id in particular section */
  convId: number
  /** Position of the conversation into the section. */
  position: number
}

/** Internal events sent by RTC. */
export interface RTCSection {
  /** Id of section */
  id: number
  /** name of section */
  name: string
  /** position of section */
  position: number
  /** read only name for favorite section */
  readOnlyName: string
  /** read only for favorite */
  readOnly: boolean
  /** read only position of favorite section */
  readOnlyPosition: number
  /** section thread details */
  conversations: RTCSectionThread[]
}

/** RTC textContent */
export interface RTCTextContent {
  /** type will be always textContent */
  type: 'textContent'
  /** data linked to the text content */
  data: RTCTextContentData
}

/** RTC textContent data  */
export type RTCTextContentData = RTCTextContentAIRephrasingQueriesData | { dataType: string, dataContent: Record<never, never> }

/** RTC textContent data types */
export enum RTCTextContentDataType {
  /** for AI rephrasing queries textContent */
  rephrasing = 'rephrasing',
}
/** Rephrasing queries for RTC texContent data entry */
export interface RTCTextContentAIRephrasingQueriesData {
  /** Type of textContent. */
  dataType: RTCTextContentDataType.rephrasing
  /** Data of the text content */
  dataContent: {
    /** version of the data model */
    version: 1
    /** rephrasing queries */
    rephrasing: string []
  }
}
/**
 * Real time favorite conversation
 */
export interface RTCFavoriteConversation {
  /** Id of conversation */
  id: number
  /** Position of conversation */
  favorite: number
}
